import React from "react";

import Button from "@/components/ui/Button.jsx";
import FolaFormattedText from "@/pages/messages/fola/components/FolaFormattedText.jsx";

function choiceVariant(variant) {
  if (variant === "default") return "default";
  if (variant === "tonal") return "tonal";
  return "ghost";
}

function cardToneClasses(tone) {
  if (tone === "critical") {
    return {
      shell:
        "border-[rgba(248,83,58,0.16)] bg-[rgba(248,83,58,0.06)] shadow-[0_0_0_1px_rgba(248,83,58,0.06),0_1.5px_0_-0.5px_rgba(26,27,35,0.06),0_3px_3px_-1.5px_rgba(26,27,35,0.06)]",
      title: "text-[rgb(var(--schemes-critical))]",
    };
  }

  return {
    shell:
      "border-[rgba(213,215,220,0.16)] bg-[rgb(var(--clr-surface-cont-highest))] shadow-[0_0_0_1px_rgba(255,255,255,0.03)]",
    title: "text-on-surface",
  };
}

function ChoiceButton({ choice, onSelect }) {
  return (
    <Button
      variant={choiceVariant(choice?.variant)}
      size="lg"
      className="w-full justify-center rounded-[12px] px-4 text-[14px] leading-5 tracking-[-0.336px]"
      onClick={() => onSelect?.(choice)}
    >
      {choice?.label}
    </Button>
  );
}

export default function FolaCardRenderer({ item, onSelectChoice }) {
  if (
    !item ||
    ![
      "fola-prompt",
      "fola-notice",
      "fola-summary-card",
      "fola-soap-card",
      "fola-reply-card",
    ].includes(item.type)
  ) {
    return null;
  }

  if (item.type === "fola-prompt") {
    const isGrid = item.layout === "grid";

    return (
      <div className="w-full max-w-[316px] rounded-[16px] border border-[rgba(213,215,220,0.16)] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4 shadow-[0_0_0_1px_rgba(255,255,255,0.03)]">
        {item.title ? (
          <h3 className="text-title-medium text-on-surface">{item.title}</h3>
        ) : null}
        {item.intro ? (
          <p className="mt-2 text-body-medium text-on-surface-var">{item.intro}</p>
        ) : null}

        {Array.isArray(item.choices) && item.choices.length > 0 ? (
          <div className={isGrid ? "mt-4 grid grid-cols-2 gap-3" : "mt-4 space-y-3"}>
            {item.choices.map((choice) => (
              <ChoiceButton
                key={choice.id}
                choice={choice}
                onSelect={onSelectChoice}
              />
            ))}
          </div>
        ) : null}

        {item.supportText ? (
          <p className="mt-3 text-body-small text-on-surface-var">{item.supportText}</p>
        ) : null}
      </div>
    );
  }

  const tone = cardToneClasses(item.tone);

  if (item.type === "fola-summary-card") {
    return (
      <div className="w-full max-w-[420px] md:max-w-[560px] rounded-[16px] border border-[rgba(213,215,220,0.16)] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4 shadow-[0_0_0_1px_rgba(255,255,255,0.03)]">
        <h3 className="text-title-medium text-on-surface">{item.title}</h3>
        {item.threadTitle ? (
          <p className="mt-1 text-body-small text-on-surface-var">{item.threadTitle}</p>
        ) : null}
        {item.summary ? (
          <FolaFormattedText
            text={item.summary}
            className="mt-3 text-body-medium text-on-surface"
          />
        ) : null}
        {Array.isArray(item.keyPoints) && item.keyPoints.length > 0 ? (
          <div className="mt-4">
            <p className="text-label-large text-on-surface">
              {item.keyPointsTitle || "Key points"}
            </p>
            <ul className="mt-2 space-y-2 text-body-medium text-on-surface-var">
              {item.keyPoints.map((point) => (
                <li key={point} className="flex gap-2">
                  <span className="mt-[8px] h-1.5 w-1.5 shrink-0 rounded-full bg-[rgb(var(--afrimed-primary))]" />
                  <FolaFormattedText text={point} className="min-w-0 flex-1" />
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {Array.isArray(item.openItems) && item.openItems.length > 0 ? (
          <div className="mt-4 rounded-[12px] bg-[rgba(86,106,139,0.08)] px-3 py-3">
            <p className="text-label-large text-on-surface">
              {item.openItemsTitle || "Open items"}
            </p>
            <ul className="mt-2 space-y-2 text-body-medium text-on-surface-var">
              {item.openItems.map((point) => (
                <li key={point} className="flex gap-2">
                  <span className="mt-[8px] h-1.5 w-1.5 shrink-0 rounded-full bg-[rgb(var(--clr-on-surface-variant))]" />
                  <FolaFormattedText text={point} className="min-w-0 flex-1" />
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  }

  if (item.type === "fola-soap-card") {
    const sections = Array.isArray(item.sections) && item.sections.length > 0
      ? item.sections
      : [
          { label: "Subjective", value: item.subjective },
          { label: "Objective", value: item.objective },
          { label: "Assessment", value: item.assessment },
          { label: "Plan", value: item.plan },
        ];

    return (
      <div className="w-full max-w-[420px] md:max-w-[560px] rounded-[16px] border border-[rgba(213,215,220,0.16)] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4 shadow-[0_0_0_1px_rgba(255,255,255,0.03)]">
        <h3 className="text-title-medium text-on-surface">{item.title}</h3>
        {item.threadTitle ? (
          <p className="mt-1 text-body-small text-on-surface-var">{item.threadTitle}</p>
        ) : null}
        <div className="mt-4 divide-y divide-[rgba(213,215,220,0.34)]">
          {sections.map((section) => (
            <section
              key={section.label}
              className="py-3 first:pt-0 last:pb-0"
            >
              <p className="text-label-large text-on-surface">{section.label}</p>
              <FolaFormattedText
                text={section.value}
                className="mt-1 text-body-medium text-on-surface-var"
              />
            </section>
          ))}
        </div>
        {item.confidenceNote ? (
          <p className="mt-4 text-body-small text-on-surface-var">{item.confidenceNote}</p>
        ) : null}
      </div>
    );
  }

  if (item.type === "fola-reply-card") {
    return (
      <div className="w-full max-w-[420px] md:max-w-[560px] rounded-[16px] border border-[rgba(213,215,220,0.16)] bg-[rgb(var(--clr-surface-cont-highest))] px-4 py-4 shadow-[0_0_0_1px_rgba(255,255,255,0.03)]">
        <h3 className="text-title-medium text-on-surface">{item.title}</h3>
        {item.threadTitle ? (
          <p className="mt-1 text-body-small text-on-surface-var">{item.threadTitle}</p>
        ) : null}
        <div className="mt-4 rounded-[12px] bg-[#DBF4FF] px-3 py-3 text-body-medium text-[#112238]">
          <FolaFormattedText text={item.body} />
        </div>
        {item.supportingText ? (
          <p className="mt-3 text-body-small text-on-surface-var">{item.supportingText}</p>
        ) : null}
        {item.cta?.id && item.cta?.label ? (
          <div className="mt-4">
            <Button
              variant="tonal"
              size="lg"
              className="w-full justify-center rounded-[12px]"
              onClick={() => onSelectChoice?.(item.cta)}
            >
              {item.cta.label}
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`w-full max-w-[316px] rounded-[16px] border px-4 py-4 ${tone.shell}`}>
      {item.title ? (
        <h3 className={`text-title-medium ${tone.title}`}>{item.title}</h3>
      ) : null}
      {item.body ? (
        <FolaFormattedText
          text={item.body}
          className="mt-2 text-body-medium text-on-surface"
        />
      ) : null}
      {item.cta?.id && item.cta?.label ? (
        <div className="mt-4">
          <Button
            variant={item.tone === "critical" ? "default" : "tonal"}
            size="lg"
            className="w-full justify-center rounded-[12px]"
            onClick={() => onSelectChoice?.(item.cta)}
          >
            {item.cta.label}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
